import React, { useContext, useState } from "react";
import { useModal } from "react-modal-hook";
import { SUBMISSION_TYPE_MODULE } from "common/constants";
import { useCreateMailingMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import Modal from "../Modal";
import { MailingForm, MailingFormData } from "./MailingForm";
import { SelectSubmissionType } from "./SubmissionTypeSelection";

type SendMailModalProps = {
  documentUploadId: string;
  propertyId: string;
  submissionTypeId?: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export function SendMailModal({
  documentUploadId,
  propertyId,
  submissionTypeId,
  onCancel,
  onSuccess,
}: SendMailModalProps) {
  const { account } = useContext(AuthContext);

  const [selectedSubmissionTypeId, setSelectedSubmissionTypeId] = useState(
    submissionTypeId ?? null,
  );

  const [createMailing] = useCreateMailingMutation();

  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const submissionTypes =
    account?.submissionTypes?.filter((type: any) =>
      type.modules?.includes(SUBMISSION_TYPE_MODULE.MAILINGS),
    ) ?? [];

  const handleSubmit = async (data: MailingFormData) => {
    if (!selectedSubmissionTypeId) {
      addErrorToast(
        "Before sending the file, please select a submission type.",
      );
      return;
    }

    track("Clicked 'Send' on Send mail modal", {
      submissionTypeId: selectedSubmissionTypeId,
      documentUploadId,
      propertyId,
      postageType: data.postageType,
      paperSize: data.paperSize,
      printInColor: data.printInColor,
      doubleSided: data.doubleSided,
    });

    void createMailing({
      variables: {
        input: {
          documentUploadId,
          propertyId,
          postageType: data.postageType,
          paperSize: data.paperSize,
          printInColor: data.printInColor,
          doubleSided: data.doubleSided,
          submissionTypeId: selectedSubmissionTypeId,
        },
      },
      onCompleted: () => {
        onSuccess();
        addSuccessToast(
          "File has been sent and a mailing record has been created!",
        );
      },
      onError: () => {
        addErrorToast("An unexpected error occurred while sending the file.");
      },
    });

    onCancel();
  };

  if (selectedSubmissionTypeId === null) {
    return (
      <SelectSubmissionType
        submissionTypes={submissionTypes}
        onSelect={setSelectedSubmissionTypeId}
      />
    );
  }

  return <MailingForm onCancel={onCancel} onSubmit={handleSubmit} />;
}

type UseSendMailModalParams = {
  documentUploadId: string;
  propertyId: string;
  submissionTypeId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
};

export function useSendMailModal({
  documentUploadId,
  propertyId,
  submissionTypeId,
  onSuccess,
  onCancel,
}: UseSendMailModalParams) {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={() => hideModal()}>
        <SendMailModal
          documentUploadId={documentUploadId}
          onCancel={() => {
            onCancel?.();
            hideModal();
          }}
          onSuccess={() => {
            onSuccess?.();
            hideModal();
          }}
          propertyId={propertyId}
          submissionTypeId={submissionTypeId}
        />
      </Modal>
    ),
    [documentUploadId, onCancel, onSuccess, propertyId, submissionTypeId],
  );

  return [showModal, hideModal] as const;
}
